import { useEffect, useState } from "react";
import { KeyRound, Save, UserPlus, Trash2, Mail, Download } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

const STATUS_STYLES = {
  "actif": "bg-emerald-500/15 text-emerald-400",
  "invitation envoyée": "bg-orange-500/15 text-orange-400",
  "invitation expirée": "bg-red-500/15 text-red-400",
};

function AdminsBlock({ currentId }) {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/admin/invite", { email });
      toast.success(`Invitation envoyée à ${email}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id, mail) => {
    if (!window.confirm(`Supprimer le compte admin ${mail} ?`)) return;
    try {
      await api.delete(`/admin/admins/${id}`);
      toast.success("Compte admin supprimé");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="card-tactical grid gap-4 p-6" data-testid="admin-accounts-section">
      <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
        <UserPlus className="h-5 w-5 text-orange-500" /> Comptes administrateurs
      </p>
      <p className="text-xs text-slate-500">
        Invitez un collaborateur par email : il recevra un lien pour activer son compte et choisir lui-même son mot de passe.
      </p>
      <form onSubmit={invite} className="flex flex-wrap gap-3">
        <input type="email" placeholder="email@exemple.fr" value={email} data-testid="admin-invite-email"
          onChange={(e) => setEmail(e.target.value)} className={inputCls + " flex-1"} required />
        <button type="submit" disabled={sending} data-testid="admin-invite-submit"
          className="flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 disabled:opacity-50">
          <Mail className="h-4 w-4" /> {sending ? "Envoi..." : "Inviter"}
        </button>
      </form>
      <div className="grid gap-2">
        {admins.map((a) => (
          <div key={a.id} data-testid={`admin-account-row-${a.id}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-700/60 bg-[#060B18] px-4 py-2.5">
            <span className="text-sm font-semibold text-slate-200">
              {a.email} {a.id === currentId && <span className="ml-1 text-xs text-slate-500">(vous)</span>}
            </span>
            <div className="flex items-center gap-2">
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[a.status] || "bg-slate-700 text-slate-300"}`}>
                {a.status}
              </span>
              {a.id !== currentId && (
                <button onClick={() => remove(a.id, a.email)} data-testid={`admin-account-delete-${a.id}`} aria-label={`Supprimer ${a.email}`}
                  className="rounded-lg border border-red-500/40 p-1.5 text-red-400 transition-all hover:bg-red-500/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AccountTab() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/admin/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Mot de passe modifié");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid max-w-2xl gap-6">
    <div className="card-tactical grid gap-4 p-6" data-testid="admin-account-section">
      <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
        <KeyRound className="h-5 w-5 text-orange-500" /> Compte administrateur
      </p>
      <p className="text-xs text-slate-500">
        Connecté : <span className="text-slate-300">{user?.email}</span> — changez le mot de passe avant l'ouverture publique du site.
      </p>
      <form onSubmit={submit} className="grid gap-4">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Mot de passe actuel
          <input type="password" value={form.current_password} data-testid="admin-current-password"
            onChange={(e) => setForm({ ...form, current_password: e.target.value })} className={inputCls + " mt-1"} required />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Nouveau mot de passe (8 caractères minimum)
          <input type="password" value={form.new_password} data-testid="admin-new-password"
            onChange={(e) => setForm({ ...form, new_password: e.target.value })} className={inputCls + " mt-1"} required minLength={8} />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Confirmer le nouveau mot de passe
          <input type="password" value={form.confirm} data-testid="admin-confirm-password"
            onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls + " mt-1"} required minLength={8} />
        </label>
        <button type="submit" disabled={loading} data-testid="admin-change-password-submit"
          className="flex w-fit items-center gap-2 rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 disabled:opacity-50">
          <Save className="h-4 w-4" /> {loading ? "Modification..." : "Changer le mot de passe"}
        </button>
      </form>
    </div>
    <div className="card-tactical grid gap-4 p-6" data-testid="admin-export-section">
      <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
        <Download className="h-5 w-5 text-orange-500" /> Sauvegarde complète
      </p>
      <p className="text-xs text-slate-500">
        Téléchargez une archive ZIP contenant toutes vos données (commandes, comptes, produits, rosters,
        actualités, newsletter, sondages, réglages…) et tous les fichiers (images, media kit…).
        Les mots de passe restent chiffrés (hash), aucun secret ni clé API n'est inclus. Utile pour une
        sauvegarde ou une réinstallation ailleurs.
      </p>
      <a href={`/api/admin/export/full`} data-testid="admin-export-full-btn"
        className="flex w-fit items-center gap-2 rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
        <Download className="h-4 w-4" /> Exporter toutes mes données
      </a>
    </div>
    <AdminsBlock currentId={user?.id} />
    </div>
  );
}
